/**
 * Sidebar —— 移动端侧边栏控制器
 *
 * 使用 Unpoly 编译器管理生命周期，替代 Alpine.js 的 x-data / x-show 实现。
 * 功能：
 *   - 汉堡按钮切换侧边栏滑入/滑出
 *   - 遮罩层点击关闭
 *   - ESC 键关闭
 *   - body 滚动锁定
 *   - 导航链接点击后自动关闭（移动端）
 */
export class Sidebar {
  /** 遮罩元素 ID */
  private static readonly OVERLAY_ID = "sidebar-overlay";

  /** 侧边栏打开时添加的 class */
  private static readonly OPEN_CLASS = "translate-x-0";
  /** 侧边栏关闭时的 class（默认状态） */
  private static readonly CLOSED_CLASS = "translate-x-full";

  /** 判断当前是否为移动端视口（< lg） */
  private static isMobile(): boolean {
    return window.innerWidth < 1024;
  }

  /** 获取侧边栏元素 */
  private static getSidebar(): HTMLElement | null {
    return document.getElementById("sidebar");
  }

  /** 获取或创建遮罩元素 */
  private static getOrCreateOverlay(): HTMLElement {
    let overlay = document.getElementById(Sidebar.OVERLAY_ID);
    if (!overlay) {
      overlay = document.createElement("div");
      overlay.id = Sidebar.OVERLAY_ID;
      overlay.className =
        "fixed inset-0 z-overlay bg-black/50 lg:hidden transition-opacity duration-300 opacity-0 pointer-events-none";
      document.body.appendChild(overlay);
    }
    return overlay;
  }

  /** 打开侧边栏 */
  private static open(): void {
    const sidebar = Sidebar.getSidebar();
    if (!sidebar) return;

    const overlay = Sidebar.getOrCreateOverlay();

    // 侧边栏滑入
    sidebar.classList.remove(Sidebar.CLOSED_CLASS);
    sidebar.classList.add(Sidebar.OPEN_CLASS);

    // 遮罩淡入
    overlay.classList.remove("opacity-0", "pointer-events-none");
    overlay.classList.add("opacity-100", "pointer-events-auto");

    // 锁定 body 滚动
    document.body.classList.add("overflow-hidden", "lg:overflow-auto");
  }

  /** 关闭侧边栏 */
  private static close(): void {
    const sidebar = Sidebar.getSidebar();
    if (!sidebar) return;

    const overlay = document.getElementById(Sidebar.OVERLAY_ID);

    // 侧边栏滑出
    sidebar.classList.remove(Sidebar.OPEN_CLASS);
    sidebar.classList.add(Sidebar.CLOSED_CLASS);

    // 遮罩淡出
    if (overlay) {
      overlay.classList.remove("opacity-100", "pointer-events-auto");
      overlay.classList.add("opacity-0", "pointer-events-none");
    }

    // 解锁 body 滚动
    document.body.classList.remove("overflow-hidden", "lg:overflow-auto");
  }

  /** 切换侧边栏状态 */
  private static toggle(): void {
    const sidebar = Sidebar.getSidebar();
    if (!sidebar) return;

    const isOpen = sidebar.classList.contains(Sidebar.OPEN_CLASS);
    if (isOpen) {
      Sidebar.close();
    } else {
      Sidebar.open();
    }
  }

  /**
   * 注册 Unpoly 编译器
   * 编译 header 中的汉堡按钮，绑定侧边栏切换事件
   */
  static compiler(): void {
    // 编译汉堡按钮
    up.compiler("[data-sidebar-toggle]", (button) => {
      const onClick = () => Sidebar.toggle();
      button.addEventListener("click", onClick);
      return () => button.removeEventListener("click", onClick);
    });

    // 编译侧边栏本身：绑定遮罩点击、ESC 关闭、导航链接点击关闭
    up.compiler("#sidebar", (sidebar) => {
      const overlay = Sidebar.getOrCreateOverlay();

      // 遮罩点击关闭
      const onOverlayClick = () => Sidebar.close();
      overlay.addEventListener("click", onOverlayClick);

      // ESC 键关闭
      const onKeydown = (e: KeyboardEvent) => {
        if (
          e.key === "Escape" &&
          Sidebar.isMobile() &&
          sidebar.classList.contains(Sidebar.OPEN_CLASS)
        ) {
          Sidebar.close();
        }
      };
      document.addEventListener("keydown", onKeydown);

      // 移动端：导航链接点击后自动关闭侧边栏
      const onNavClick = (e: Event) => {
        const target = e.target as HTMLElement;
        if (target.closest("[data-nav-id]") && Sidebar.isMobile()) {
          Sidebar.close();
        }
      };
      sidebar.addEventListener("click", onNavClick);

      // Unpoly 析构函数：清理所有事件监听
      return () => {
        overlay.removeEventListener("click", onOverlayClick);
        document.removeEventListener("keydown", onKeydown);
        sidebar.removeEventListener("click", onNavClick);
      };
    });
  }
}
